import { motion } from "framer-motion";
import { useParams, Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/contexts/LanguageContext";
import { ArrowRight, ArrowLeft } from "lucide-react";
import stainlessRailing from "@/assets/stainless-railing.jpg";
import stainlessDoors from "@/assets/stainless-doors.jpg";
import stainlessFacade from "@/assets/stainless-facade.jpg";
import stainlessKitchen from "@/assets/stainless-kitchen.jpg";
import stainlessSink from "@/assets/stainless-sink.jpg";
import stainlessDecor from "@/assets/stainless-decor.jpg";

const StainlessSteelProductDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language, isRTL } = useLanguage();

  const content = {
    ar: {
      backToProducts: "العودة لمنتجات الستانلس ستيل",
      contactUs: "تواصل معنا للطلب",
      features: "المميزات",
      ourProjects: "مشاريعنا وأعمالنا",
      products: {
        "railings": {
          title: "درابزينات فاخرة",
          description: "درابزينات استانلس ستيل فاخرة للسلالم والشرفات",
          longDescription: "درابزينات استانلس ستيل فاخرة للسلالم والشرفات بتصميمات عصرية وأنيقة. تتميز بالمتانة العالية ومقاومة الصدأ مع الحفاظ على المظهر اللامع لسنوات طويلة.",
          features: ["متانة عالية", "مقاومة للصدأ", "مظهر لامع", "تصاميم عصرية", "صيانة سهلة"],
          images: [stainlessRailing, stainlessDoors, stainlessFacade]
        },
        "doors": {
          title: "أبواب وبوابات",
          description: "أبواب وبوابات أمنية من الاستانلس ستيل",
          longDescription: "أبواب وبوابات أمنية من الاستانلس ستيل بتصاميم عصرية توفر الحماية والأمان مع المظهر الراقي. مناسبة للمباني التجارية والسكنية مع أنظمة قفل متطورة.",
          features: ["أمان عالي", "مظهر راقي", "أنظمة قفل متطورة", "مقاومة للعوامل الجوية", "عمر طويل"],
          images: [stainlessDoors, stainlessRailing, stainlessFacade]
        },
        "facades": {
          title: "واجهات المباني",
          description: "واجهات استانلس ستيل للمباني التجارية والفنادق",
          longDescription: "واجهات استانلس ستيل للمباني التجارية والفنادق تضفي مظهراً عصرياً وفاخراً. تتميز بمقاومتها للعوامل الجوية وسهولة الصيانة مع الحفاظ على اللمعان.",
          features: ["مظهر عصري", "مقاومة العوامل الجوية", "سهولة الصيانة", "لمعان دائم", "عزل ممتاز"],
          images: [stainlessFacade, stainlessDoors, stainlessRailing]
        },
        "kitchens": {
          title: "تجهيزات المطابخ",
          description: "تجهيزات متكاملة للمطابخ الصناعية والتجارية",
          longDescription: "تجهيزات متكاملة للمطابخ الصناعية والتجارية من الاستانلس ستيل عالي الجودة. تلبي معايير النظافة والسلامة الغذائية مع متانة تتحمل الاستخدام المكثف.",
          features: ["معايير النظافة", "سلامة غذائية", "متانة عالية", "سهولة التنظيف", "مقاومة الحرارة"],
          images: [stainlessKitchen, stainlessSink, stainlessFacade]
        },
        "sinks": {
          title: "أحواض وطاولات",
          description: "أحواض وطاولات استانلس ستيل للمطاعم والمستشفيات",
          longDescription: "أحواض وطاولات استانلس ستيل خاصة بالمطاعم والمستشفيات والمختبرات. مصممة لتحمل الاستخدام المكثف مع سهولة التنظيف والتعقيم.",
          features: ["سهولة التعقيم", "تحمل الاستخدام المكثف", "مقاومة البكتيريا", "تصاميم عملية", "جودة طبية"],
          images: [stainlessSink, stainlessKitchen, stainlessDecor]
        },
        "decorations": {
          title: "ديكورات فخمة",
          description: "ديكورات داخلية وخارجية من الاستانلس ستيل",
          longDescription: "ديكورات داخلية وخارجية من الاستانلس ستيل تضيف لمسة من الفخامة والأناقة. تصاميم مبتكرة تناسب المساحات المختلفة مع إمكانية التخصيص حسب الطلب.",
          features: ["تصاميم مبتكرة", "فخامة عالية", "تخصيص حسب الطلب", "مظهر أنيق", "متانة دائمة"],
          images: [stainlessDecor, stainlessRailing, stainlessFacade]
        }
      }
    },
    en: {
      backToProducts: "Back to Stainless Steel Products",
      contactUs: "Contact Us to Order",
      features: "Features",
      ourProjects: "Our Projects & Work",
      products: {
        "railings": {
          title: "Luxury Railings",
          description: "Luxury stainless steel railings for stairs and balconies",
          longDescription: "Luxury stainless steel railings for stairs and balconies with modern and elegant designs. Featuring high durability and rust resistance while maintaining a shiny appearance for years.",
          features: ["High durability", "Rust resistant", "Shiny appearance", "Modern designs", "Easy maintenance"],
          images: [stainlessRailing, stainlessDoors, stainlessFacade]
        },
        "doors": {
          title: "Doors & Gates",
          description: "Security doors and gates made of stainless steel",
          longDescription: "Security doors and gates made of stainless steel with modern designs providing protection and safety with an upscale appearance. Suitable for commercial and residential buildings with advanced locking systems.",
          features: ["High security", "Upscale appearance", "Advanced locking systems", "Weather resistant", "Long lifespan"],
          images: [stainlessDoors, stainlessRailing, stainlessFacade]
        },
        "facades": {
          title: "Building Facades",
          description: "Stainless steel facades for commercial buildings and hotels",
          longDescription: "Stainless steel facades for commercial buildings and hotels adding a modern and luxurious appearance. Featuring weather resistance and easy maintenance while maintaining shine.",
          features: ["Modern appearance", "Weather resistant", "Easy maintenance", "Lasting shine", "Excellent insulation"],
          images: [stainlessFacade, stainlessDoors, stainlessRailing]
        },
        "kitchens": {
          title: "Kitchen Equipment",
          description: "Complete equipment for industrial and commercial kitchens",
          longDescription: "Complete equipment for industrial and commercial kitchens made of high-quality stainless steel. Meeting hygiene and food safety standards with durability to withstand intensive use.",
          features: ["Hygiene standards", "Food safety", "High durability", "Easy to clean", "Heat resistant"],
          images: [stainlessKitchen, stainlessSink, stainlessFacade]
        },
        "sinks": {
          title: "Sinks & Tables",
          description: "Stainless steel sinks and tables for restaurants and hospitals",
          longDescription: "Stainless steel sinks and tables for restaurants, hospitals, and laboratories. Designed to withstand intensive use with easy cleaning and sterilization.",
          features: ["Easy sterilization", "Intensive use resistant", "Bacteria resistant", "Practical designs", "Medical grade"],
          images: [stainlessSink, stainlessKitchen, stainlessDecor]
        },
        "decorations": {
          title: "Luxury Decorations",
          description: "Interior and exterior decorations made of stainless steel",
          longDescription: "Interior and exterior decorations made of stainless steel adding a touch of luxury and elegance. Innovative designs suitable for different spaces with customization options.",
          features: ["Innovative designs", "High luxury", "Custom options", "Elegant appearance", "Lasting durability"],
          images: [stainlessDecor, stainlessRailing, stainlessFacade]
        }
      }
    }
  };

  const t = content[language];
  const product = slug ? t.products[slug as keyof typeof t.products] : null;

  if (!product) {
    return (
      <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
        <Navbar />
        <div className="container mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold mb-4">{language === "ar" ? "المنتج غير موجود" : "Product Not Found"}</h1>
          <Link to="/stainless-steel">
            <Button className="bg-primary hover:bg-primary/90">{t.backToProducts}</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const ArrowIcon = isRTL ? ArrowRight : ArrowLeft;

  return (
    <div className="min-h-screen bg-background" dir={isRTL ? "rtl" : "ltr"}>
      <Navbar />
      <div className="container mx-auto px-4 py-12">
        <Link to="/stainless-steel" className="inline-flex items-center gap-2 text-primary hover:text-primary/80 mb-8 font-kufi">
          <ArrowIcon className="w-4 h-4" />
          {t.backToProducts}
        </Link>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="text-center mb-12">
          <h1 className="text-4xl font-kufi font-bold text-primary mb-4">{product.title}</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">{product.description}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="bg-card rounded-xl shadow-lg p-8 mb-12">
          <p className="text-lg leading-relaxed text-card-foreground">{product.longDescription}</p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.2 }} className="bg-primary/5 rounded-xl p-8 mb-12">
          <h2 className="text-2xl font-kufi font-bold text-primary mb-6">{t.features}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {product.features.map((feature, index) => (
              <div key={index} className="flex items-center gap-3 bg-card p-4 rounded-lg shadow-sm">
                <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0" />
                <span className="text-card-foreground">{feature}</span>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }} className="mb-12">
          <h2 className="text-2xl font-kufi font-bold text-primary mb-8 text-center">{t.ourProjects}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {product.images.map((image, index) => (
              <motion.div key={index} initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.3, delay: index * 0.1 }} className="group relative overflow-hidden rounded-xl shadow-lg aspect-[4/3]">
                <img src={image} alt={`${product.title} ${index + 1}`} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.4 }} className="text-center bg-primary/10 rounded-xl p-8">
          <Link to="/contact">
            <Button size="lg" className="bg-primary hover:bg-primary/90 text-white font-kufi text-lg px-8">{t.contactUs}</Button>
          </Link>
        </motion.div>
      </div>
      <Footer />
    </div>
  );
};

export default StainlessSteelProductDetail;
